import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { KB_LIFT_MS } from '../../components/common/keyboardOverlayStyles';

/**
 * Smooth-slide helper for `useModalKeyboardAnticipation` (RD-DEV-019; always on).
 * Plain DOM, no React. Timers, never `transitionend` (iOS drops end events —
 * see RELEASE_FALLBACK_MS in the hook).
 */

/** The longest flip motion (the lift, KB_LIFT_MS); shorter flips just clear later. */
const MOTION_MS = KB_LIFT_MS;
const MOTION_PAD_MS = 80;
const PAN_PROBE_MS = 450;
/**
 * Stall-aware start (KAN-930 step 6, EXPERIMENTAL, rest cards' content reveal
 * only). Device theory: iOS stalls the main thread while it brings the
 * keyboard up, so a FLIP that starts at rAF #2 has mostly "played" during the
 * stall and reads as an instant scroll. With `startAfterStall` the counter-
 * transform still lands at rAF #1, but the animation starts only once two
 * consecutive frames arrive < STALL_FRAME_GAP_MS apart OR the first
 * `visualViewport` resize — capped at STALL_CAP_MS. `kbslide-stall` logs the
 * wait so a `?kbdebug=1` recording tells us whether it helps. If nothing
 * fires, the cap starts it: never worse than a late start of today's flip.
 */
export const STALL_FRAME_GAP_MS = 25;
export const STALL_CAP_MS = 300;

/** Logs iOS's viewport offset ~450ms after an anchor: non-zero = it panned anyway. */
export function probePan(mode) {
  return setTimeout(() => {
    kbLog('kbslide-pan', { mode, vvOffsetTop: window.visualViewport?.offsetTop ?? null });
  }, PAN_PROBE_MS);
}

/**
 * FLIP: layout has already moved up by `delta` (= restingTop − newTop); paint
 * the element back at its old spot, then animate it to the new one.
 *
 * The counter-transform is DEFERRED to rAF #1, never written in the focus
 * handler: iOS (and the inline guard) read element rects INCLUDING transforms
 * right after the focus handlers, so a synchronous counter-transform makes the
 * field look un-lifted and iOS pans anyway. rAF #1 runs before that frame's
 * paint, so no frame ever shows the jump; rAF #2 starts the animation to 0.
 *
 * SINGLE OWNER of `transition`: the shell (dialog) / the sheet lift own the
 * composed list. The dialog case captures it at call time, writes `'none'` only
 * at rAF #1 (instant counter-jump) and writes the captured value back at rAF #2;
 * it never writes transition again. A non-dialog node (tuck followers, content
 * children) passes `{ transition }` — its animate value — and restores its own
 * saved value afterwards.
 * @returns {(() => void) & { settle: () => void }} cancel; `settle` cancels only
 * a flip whose frames have not run yet (a RUNNING transition finishes).
 */
export function flipSlide(el, delta, { transition, startAfterStall = false } = {}) {
  const noop = () => {};
  noop.settle = noop;
  if (!el || !delta) return noop;
  let savedTransform = el.style.transform;
  let savedTransition = el.style.transition; // captured at rAF #1 (the shell's CURRENT list)
  let animate = transition;
  const ownsTransition = transition != null;
  let wroteNone = false;
  let counter = null; // the read-back counter value (ours while unchanged)
  let zero = null; // what the engine reads back after we write translateY(0) ('translateY(0px)' in real browsers)
  let timer = null;
  let raf1 = null;
  let raf2 = null;
  let stallCleanup = null;
  // Lenient: clear only OUR finished transform (a leftover translateY(0) would
  // leave a containing block); a later writer's transform is never touched.
  const clear = () => {
    if (zero != null && el.style.transform === zero) el.style.transform = savedTransform;
    if (ownsTransition && el.style.transition === animate) el.style.transition = savedTransition;
  };
  raf1 = requestAnimationFrame(() => {
    raf1 = null;
    savedTransform = el.style.transform;
    savedTransition = el.style.transition;
    if (animate == null) animate = savedTransition;
    el.style.transition = 'none';
    wroteNone = true;
    el.style.transform = `translateY(${delta}px)`;
    counter = el.style.transform;
    void el.offsetHeight; // force reflow so the counter-transform is the "from"
    const go = () => {
      raf2 = null;
      // Write back ONLY while still ours: a newer writer's value survives.
      if (el.style.transition === 'none') el.style.transition = animate;
      wroteNone = false;
      if (el.style.transform === counter) {
        el.style.transform = 'translateY(0)';
        zero = el.style.transform; // compare against the READ-BACK value, never the literal
      }
      timer = setTimeout(() => { timer = null; clear(); }, MOTION_MS + MOTION_PAD_MS);
    };
    if (!startAfterStall) { raf2 = requestAnimationFrame(go); return; }
    stallCleanup = waitOutStall((reason, ms) => {
      stallCleanup = null;
      kbLog('kbslide-stall', { ms, reason });
      go();
    }, (id) => { raf2 = id; });
  });
  const cancel = () => {
    if (raf1 != null) cancelAnimationFrame(raf1);
    if (raf2 != null) cancelAnimationFrame(raf2);
    if (timer != null) clearTimeout(timer);
    if (stallCleanup) { stallCleanup(); stallCleanup = null; }
    raf1 = null; raf2 = null; timer = null;
    if (wroteNone) {
      if (el.style.transition === 'none') el.style.transition = savedTransition;
      wroteNone = false;
      if (el.style.transform === counter) el.style.transform = savedTransform;
    }
    clear();
  };
  cancel.settle = () => { if (raf1 != null || raf2 != null || stallCleanup) cancel(); };
  return cancel;
}

/**
 * Calls `done(reason, waitedMs)` once: two frames < STALL_FRAME_GAP_MS apart,
 * the first visualViewport resize, or STALL_CAP_MS. Returns a cancel.
 * `onRaf(id)` mirrors the pending rAF id for the caller's bookkeeping.
 */
function waitOutStall(done, onRaf) {
  const t0 = Date.now();
  let prev = t0;
  let raf = 0;
  let finished = false;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const end = (reason) => {
    if (finished) return;
    finished = true;
    cleanup(); // eslint-disable-line no-use-before-define
    done(reason, Date.now() - t0);
  };
  const onResize = () => end('vv');
  const cap = setTimeout(() => end('cap'), STALL_CAP_MS);
  const frame = () => {
    const now = Date.now();
    const gap = now - prev;
    prev = now;
    if (gap < STALL_FRAME_GAP_MS) { end('frames'); return; }
    raf = requestAnimationFrame(frame);
    onRaf(raf);
  };
  function cleanup() {
    clearTimeout(cap);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    onRaf(null);
    if (vv && vv.removeEventListener) vv.removeEventListener('resize', onResize);
  }
  if (vv && vv.addEventListener) vv.addEventListener('resize', onResize);
  // The first frame after rAF #1 measures the first gap.
  raf = requestAnimationFrame(frame);
  onRaf(raf);
  return () => { finished = true; cleanup(); };
}
